import { getAnalytics } from "@tm/insurance/analytics";
import {
  IntersectionObserverData,
  useIntersectionObserver,
} from "@tm/ui/hooks";
import React, { useCallback, useRef } from "react";
import FormattedText, { TextNodeType } from "../FormattedText/FormattedText";
import {
  WhyBuySectionCustomNumber,
  WhyBuySectionCustomNumberInfo,
  WhyBuySectionCustomNumbers,
  WhyBuySectionCustomNumberTitle,
  WhyBuySectionDescription,
  WhyBuySectionStyle,
  WhyBuySectionTitle,
} from "./WhyBuySection.style";

export interface IWhyBuySection {
  title: TextNodeType;
  description: TextNodeType;
  customNumbers: Array<{
    title: string;
    info: string;
  }>;
}

interface WhyBuySectionProps {
  whyBuySectionData: IWhyBuySection;
}

const WhyBuySection: React.FC<WhyBuySectionProps> = ({ whyBuySectionData }) => {
  const analytics = getAnalytics();
  const hasTracked = useRef<boolean>(false);

  // Track component view when it comes into viewport
  const handleIntersection = useCallback((data: IntersectionObserverData) => {
    if (!hasTracked.current) {
      analytics?.trackCommonLandingEvents({
        actionType: "why_buy_section_seen",
      });
    }
    hasTracked.current = true;
  }, []);

  const { ref: whyBuySectionRef } = useIntersectionObserver(
    handleIntersection,
    {
      threshold: 0.5, // Trigger when 50% of component is visible
      triggerOnLeaveViewport: true,
    }
  );

  return (
    <WhyBuySectionStyle ref={whyBuySectionRef}>
      <WhyBuySectionTitle>
        <FormattedText value={whyBuySectionData.title} />
      </WhyBuySectionTitle>
      <WhyBuySectionDescription>
        <FormattedText value={whyBuySectionData.description} />
      </WhyBuySectionDescription>
      <WhyBuySectionCustomNumbers>
        {whyBuySectionData.customNumbers.map((item, index) => (
          <WhyBuySectionCustomNumber key={index}>
            <WhyBuySectionCustomNumberTitle>
              {item.title}
            </WhyBuySectionCustomNumberTitle>
            <WhyBuySectionCustomNumberInfo>
              {item.info}
            </WhyBuySectionCustomNumberInfo>
          </WhyBuySectionCustomNumber>
        ))}
      </WhyBuySectionCustomNumbers>
    </WhyBuySectionStyle>
  );
};

export default WhyBuySection;
